import { loadModuleFromFile, loadModuleFromUrl, getAllInstalledModules, setModuleState, uninstallModule, loadCachedModules, type ModuleLoadResult } from './modules/services/CustomModuleLoader';
import type { InstalledModuleMeta, ModuleState } from './modules/module-types';
import { slotManager } from './modules/services/SlotManager';
import { sovereignModuleSlotManager } from './modules/SovereignModuleSlotManager.js';
import { dynamicI18nEngine } from './i18n/services/DynamicI18nEngine';
import type { LocalePackMeta } from './i18n/services/DynamicI18nEngine';
import React, { useState, useEffect } from 'react';
import { UserProfileData } from './OnboardingModal';
import {
  HostDirectoryService,
  globalVaultDB,
  globalIdentityRegistry,
  globalPluginActionBus,
  globalCommandRegistry,
  globalNotificationStore,
  globalWorkspaceStore,
  GenesisProvisioner,
  reconcileBackupPayload,
  type SettingsAccess,
  type VerifiedPrincipal,
  type DomainTabAccess,
} from 'mesh-microkernel';
import { THEME_PRESETS, ThemePreset, applyThemeTokens } from '../theme/themeEngine';
import type { ExecutionContext } from '../identity/execution-context';
import { HelperNodeRegistrationModal } from './compute/HelperNodeRegistrationModal';
import { AdminLeaseApprovalModal } from './compute/AdminLeaseApprovalModal';
import { ModelOrchestrationDashboard } from './compute/ModelOrchestrationDashboard.js';
import type { ParticipationProposal, ResourceGrant } from '../mesh/compute/capacity-lease-contracts';
import { AvatarPhotoEditor } from './identity/AvatarPhotoEditor.js';
import { ExpertiseSelfRatingPanel } from './identity/ExpertiseSelfRatingPanel.js';
import { UserManagementPanel } from './auth/components/UserManagementPanel';
import { multiAccountVault } from './auth/multi-account-vault';
import type { SpecialisationSelfRating } from '../identity/domain/profile-claims';
import { CANONICAL_18_PLUGIN_CATALOG } from '../workspace/plugin-manifests.js';
import { globalWorkspaceBootstrapper } from '../workspace/workspace-bootstrap.js';
import { searchSettings, SettingsSearchItem } from './settings-search-index.js';
import { useTranslation } from '../i18n/useTranslation.js';
import { SUPPORTED_LOCALES, type Bcp47LocaleTag } from '../i18n/locale-contracts.js';
import { globalLanguageRegistryService } from '../i18n/language-registry-service.js';
import { ComfyMeshCoordinatorSection } from './compute/ComfyMeshCoordinatorSection';
import {
  UserIcon,
  UsersIcon,
  ServerIcon,
  CpuIcon,
  HardDriveIcon,
  LayersIcon,
  ShieldIcon,
  ZapIcon,
  ActivityIcon,
  CopyIcon,
  CheckIcon,
  RefreshIcon,
  LinkIcon,
  DownloadIcon,
  TrashIcon,
  VideoIcon,
  CloudUploadIcon,
} from './icons';
import { ExtensionManagerPane } from './settings/ExtensionManagerPane';

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  nodeId: string;
  directory: HostDirectoryService;
  profile: UserProfileData;
  onUpdateProfile: (updated: UserProfileData) => void;
  onResetNodeCache: () => void;
  currentTheme: ThemePreset;
  onSelectTheme: (preset: ThemePreset) => void;
  onOpenGenesisSetup?: () => void;
  isOwner?: boolean;
  settingsAccess?: SettingsAccess;
  verifiedPrincipal?: VerifiedPrincipal;
  initialTab?: SettingsTab;
  executionContext?: ExecutionContext;
}

export type SettingsTab =
  | 'identity'
  | 'members'
  | 'topology'
  | 'compute'
  | 'vault'
  | 'modules'
  | 'security'
  | 'commands'
  | 'observability'
  | 'ambient';

// ─── Sub-component: SovereignModuleControllerSection ─────────────────────────
// Renders inside the Modules & Collaboration settings tab.
// Replaces legacy file-drop ingestion with the verified server catalog.

interface ServerModuleItem {
  id: string;
  name: string;
  version: string;
  category: string;
  systemModule?: boolean;
  slots?: Array<{ slot: string; label?: string; icon?: string }>;
  description: string;
  author: string;
  permissions: string[];
  icon?: string;
  entrypoint?: string;
  minServerVersion?: string;
  contentHashes?: Record<string, string>;
  signature?: string;
  isEnabled: boolean;
  isCompatible: boolean;
  verificationError?: string;
  configSchema?: {
    global?: Record<string, any>;
    tenant?: Record<string, any>;
    user?: Record<string, any>;
  };
}

const SovereignModuleControllerSection: React.FC = () => {
  const [serverModules, setServerModules] = React.useState<ServerModuleItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [actionLoadingId, setActionLoadingId] = React.useState<string | null>(null);
  const [feedback, setFeedback] = React.useState<string | null>(null);

  // User-Scoped Module Configuration State
  const [userConfigMod, setUserConfigMod] = React.useState<ServerModuleItem | null>(null);
  const [userConfigValues, setUserConfigValues] = React.useState<Record<string, any>>({});
  const [userConfigSaving, setUserConfigSaving] = React.useState(false);
  const [userConfigFeedback, setUserConfigFeedback] = React.useState<string | null>(null);

  const openUserConfig = async (mod: ServerModuleItem) => {
    setUserConfigMod(mod);
    setUserConfigFeedback(null);
    const initial: Record<string, any> = {};
    if (mod.configSchema?.user) {
      for (const [k, p] of Object.entries(mod.configSchema.user)) {
        if (p.default !== undefined) initial[k] = p.default;
      }
    }
    setUserConfigValues(initial);

    try {
      const res = await fetch(`/api/v1/me/modules/${mod.id}/config`, {
        headers: { 'x-mesh-role': 'mesh_user', 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.ok && data.data?.config) {
        setUserConfigValues((prev) => ({ ...prev, ...data.data.config }));
      }
    } catch (err: any) {
      console.warn('[UserSettingsModal] Failed to fetch user module config:', err.message);
    }
  };

  const saveUserConfig = async () => {
    if (!userConfigMod) return;
    setUserConfigSaving(true);
    setUserConfigFeedback(null);

    try {
      const res = await fetch(`/api/v1/me/modules/${userConfigMod.id}/config`, {
        method: 'PATCH',
        headers: { 'x-mesh-role': 'mesh_user', 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: userConfigValues }),
      });
      const data = await res.json();
      if (data.ok) {
        setUserConfigFeedback('Preferences saved securely!');
        setTimeout(() => setUserConfigMod(null), 1500);
      } else {
        setUserConfigFeedback(`Error: ${data.error || 'Failed to save preferences'}`);
      }
    } catch (err: any) {
      setUserConfigFeedback(`Error: ${err.message}`);
    } finally {
      setUserConfigSaving(false);
    }
  };

  const fetchServerModules = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/v1/admin/modules/available', {
        headers: { 'x-mesh-role': 'mesh_owner', 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.ok && Array.isArray(data.data?.modules)) {
          setServerModules(data.data.modules);
          sovereignModuleSlotManager.updateActiveModules(data.data.modules);
        }
      }
    } catch (err: any) {
      console.warn('[UserSettingsModal] Failed to fetch server modules:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchServerModules();
  }, [fetchServerModules]);

  const handleToggle = async (mod: ServerModuleItem) => {
    setActionLoadingId(mod.id);
    setFeedback(null);
    try {
      const endpoint = mod.isEnabled
        ? `/api/v1/admin/modules/${mod.id}/disable`
        : `/api/v1/admin/modules/${mod.id}/enable`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'x-mesh-role': 'mesh_owner', 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Operation failed');

      setFeedback(mod.isEnabled ? `Deactivated ${mod.name}` : `Activated ${mod.name}`);
      await fetchServerModules();
    } catch (err: any) {
      setFeedback(`Error: ${err.message}`);
    } finally {
      setActionLoadingId(null);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const handleUpgrade = async (mod: ServerModuleItem) => {
    setActionLoadingId(mod.id);
    setFeedback(null);
    try {
      const res = await fetch(`/api/v1/admin/modules/${mod.id}/upgrade`, {
        method: 'POST',
        headers: { 'x-mesh-role': 'mesh_owner', 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Upgrade failed');

      setFeedback(`Upgraded ${mod.name} to v${mod.version} (Verified Ed25519)`);
      await fetchServerModules();
    } catch (err: any) {
      setFeedback(`Error: ${err.message}`);
    } finally {
      setActionLoadingId(null);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const handleUninstall = async (mod: ServerModuleItem) => {
    if (mod.systemModule) {
      setFeedback(`Cannot uninstall system module "${mod.name}"`);
      setTimeout(() => setFeedback(null), 4000);
      return;
    }

    if (!window.confirm(`Uninstall "${mod.name}"?`)) return;

    setActionLoadingId(mod.id);
    setFeedback(null);
    try {
      const res = await fetch(`/api/v1/admin/modules/${mod.id}/uninstall`, {
        method: 'POST',
        headers: { 'x-mesh-role': 'mesh_owner', 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Uninstall failed');

      setFeedback(`Uninstalled ${mod.name}`);
      await fetchServerModules();
    } catch (err: any) {
      setFeedback(`Error: ${err.message}`);
    } finally {
      setActionLoadingId(null);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const cardStyle: React.CSSProperties = {
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    paddingTop: '16px',
    marginTop: '8px',
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: '#60a5fa', margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>⚡ Sovereign Module Hub &amp; Microkernel Controller</span>
            <span style={{ fontSize: 9, background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '1px 6px', borderRadius: 4, fontFamily: 'monospace' }}>
              v1.2.0
            </span>
          </h3>
          <p style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 0 0' }}>
            Verified server-side registry. Cryptographically validated with Ed25519 signatures and SHA-256 asset hashes.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchServerModules}
          disabled={loading}
          style={{
            padding: '4px 10px',
            borderRadius: 6,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: '#38bdf8',
            fontSize: 10,
            cursor: 'pointer',
            fontWeight: 700,
          }}
        >
          {loading ? '⏳' : '🔄 Sync'}
        </button>
      </div>

      {/* Zero-Upload Security Invariant Banner */}
      <div
        style={{
          border: '1px solid rgba(99, 102, 241, 0.3)',
          borderRadius: 8,
          padding: '10px 14px',
          background: 'rgba(99, 102, 241, 0.08)',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
        }}
      >
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#f8fafc' }}>
            Zero-Upload Security Invariant Active
          </div>
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>
            Direct bundle uploads are permanently disabled. All verified capabilities are ingested strictly via the server-governed repository (<code style={{ color: '#818cf8' }}>server/modules-available/</code>).
          </div>
        </div>
        <span style={{ fontSize: 9, background: 'rgba(16, 185, 129, 0.2)', color: '#34d399', border: '1px solid #10b981', padding: '2px 6px', borderRadius: 4, fontWeight: 700, whiteSpace: 'nowrap' }}>
          ✓ Invariant Active
        </span>
      </div>

      {feedback && (
        <div style={{ padding: '6px 12px', borderRadius: 6, background: feedback.startsWith('Error') ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)', color: feedback.startsWith('Error') ? '#fca5a5' : '#34d399', fontSize: 11, fontWeight: 700, marginBottom: 10 }}>
          {feedback}
        </div>
      )}

      {/* Catalog Module Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {serverModules.map((mod) => (
          <div
            key={mod.id}
            data-testid={`server-module-${mod.id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: '10px 14px',
              borderRadius: 8,
              border: mod.isEnabled ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid rgba(255,255,255,0.08)',
              background: mod.isEnabled ? 'rgba(56, 189, 248, 0.04)' : 'rgba(255,255,255,0.02)',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 16 }}>{mod.icon || '📦'}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#f8fafc' }}>{mod.name}</span>
                <span style={{ fontSize: 9, color: '#64748b' }}>v{mod.version}</span>
                {mod.systemModule ? (
                  <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', fontWeight: 600, fontFamily: 'monospace' }}>
                    System Core
                  </span>
                ) : (
                  <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(255, 255, 255, 0.08)', color: '#cbd5e1', fontFamily: 'monospace' }}>
                    Optional
                  </span>
                )}
                <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(16,185,129,0.15)', color: '#34d399', fontFamily: 'monospace' }}>
                  ✓ Ed25519
                </span>
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{mod.description}</div>

              {/* Declared Slots */}
              {mod.slots && mod.slots.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                  <span style={{ fontSize: 9, color: '#64748b', fontFamily: 'monospace' }}>Slots:</span>
                  {mod.slots.map((s) => (
                    <span key={s.slot} style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: 'rgba(6,182,212,0.15)', color: '#38bdf8', fontFamily: 'monospace' }}>
                      {s.slot}
                    </span>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                {mod.permissions?.map((p) => (
                  <span key={p} style={{ fontSize: 9, padding: '1px 4px', borderRadius: 3, background: 'rgba(255,255,255,0.06)', color: '#94a3b8', fontFamily: 'monospace' }}>
                    {p}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              {/* User Config Button */}
              {mod.configSchema?.user && Object.keys(mod.configSchema.user).length > 0 && (
                <button
                  type="button"
                  onClick={() => openUserConfig(mod)}
                  title="Configure Personal Settings"
                  style={{
                    padding: '5px 8px',
                    borderRadius: 6,
                    border: '1px solid rgba(56, 189, 248, 0.3)',
                    fontSize: 10,
                    cursor: 'pointer',
                    background: 'rgba(56, 189, 248, 0.1)',
                    color: '#38bdf8',
                  }}
                >
                  ⚙️
                </button>
              )}

              {/* Upgrade Button */}
              <button
                type="button"
                onClick={() => handleUpgrade(mod)}
                disabled={actionLoadingId === mod.id}
                title="Re-verify signature & upgrade"
                style={{
                  padding: '5px 8px',
                  borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.1)',
                  fontSize: 10,
                  cursor: 'pointer',
                  background: 'rgba(255,255,255,0.05)',
                  color: '#94a3b8',
                }}
              >
                🔄
              </button>

              {/* Uninstall Button */}
              <button
                type="button"
                onClick={() => handleUninstall(mod)}
                disabled={actionLoadingId === mod.id || mod.systemModule}
                title={mod.systemModule ? 'System module cannot be uninstalled' : 'Uninstall module'}
                style={{
                  padding: '5px 8px',
                  borderRadius: 6,
                  border: '1px solid rgba(239,68,68,0.2)',
                  fontSize: 10,
                  cursor: mod.systemModule ? 'not-allowed' : 'pointer',
                  background: mod.systemModule ? 'rgba(255,255,255,0.02)' : 'rgba(239,68,68,0.1)',
                  color: mod.systemModule ? '#475569' : '#f87171',
                }}
              >
                🗑
              </button>

              {/* Toggle / Activate Button */}
              <button
                type="button"
                onClick={() => handleToggle(mod)}
                disabled={actionLoadingId === mod.id}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: 'none',
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: mod.isEnabled ? 'rgba(239,68,68,0.2)' : 'linear-gradient(135deg, #0284c7, #2563eb)',
                  color: mod.isEnabled ? '#fca5a5' : '#ffffff',
                }}
              >
                {actionLoadingId === mod.id ? '⏳' : mod.isEnabled ? '⏸ Deactivate' : '⚡ Activate'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* User Module Config Modal */}
      {userConfigMod && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(8px)',
            padding: 16,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 480,
              background: '#0f172a',
              border: '1px solid rgba(56, 189, 248, 0.3)',
              borderRadius: 16,
              padding: 20,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              color: '#f8fafc',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 12, marginBottom: 16 }}>
              <div>
                <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#38bdf8' }}>
                  ⚙️ Personal Settings: {userConfigMod.name}
                </h4>
                <p style={{ margin: '2px 0 0 0', fontSize: 10, color: '#94a3b8' }}>
                  Tier-3 User Preferences (Overrides Global and Tenant Settings)
                </p>
              </div>
              <button
                type="button"
                onClick={() => setUserConfigMod(null)}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: 16, cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '60vh', overflowY: 'auto' }}>
              {userConfigMod.configSchema?.user &&
                Object.entries(userConfigMod.configSchema.user).map(([key, prop]: [string, any]) => {
                  const currentValue = userConfigValues[key] !== undefined ? userConfigValues[key] : prop.default;

                  return (
                    <div
                      key={key}
                      style={{
                        padding: 10,
                        borderRadius: 8,
                        background: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <code style={{ fontSize: 11, color: '#38bdf8', fontWeight: 600 }}>{key}</code>
                        {prop.sensitive && (
                          <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24' }}>
                            🔒 Encrypted
                          </span>
                        )}
                      </div>
                      {prop.description && (
                        <p style={{ margin: '0 0 6px 0', fontSize: 10, color: '#94a3b8' }}>{prop.description}</p>
                      )}

                      {prop.type === 'boolean' ? (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 11 }}>
                          <input
                            type="checkbox"
                            checked={Boolean(currentValue)}
                            onChange={(e) =>
                              setUserConfigValues((prev) => ({ ...prev, [key]: e.target.checked }))
                            }
                          />
                          <span>{Boolean(currentValue) ? 'Enabled' : 'Disabled'}</span>
                        </label>
                      ) : (
                        <input
                          type={prop.sensitive ? 'password' : 'text'}
                          value={currentValue ?? ''}
                          placeholder={prop.sensitive ? 'Enter sensitive token...' : 'Value'}
                          onChange={(e) =>
                            setUserConfigValues((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                          style={{
                            width: '100%',
                            padding: '6px 10px',
                            borderRadius: 6,
                            background: 'rgba(0,0,0,0.4)',
                            border: '1px solid rgba(255,255,255,0.15)',
                            color: '#fff',
                            fontSize: 11,
                            fontFamily: 'monospace',
                            boxSizing: 'border-box',
                          }}
                        />
                      )}
                    </div>
                  );
                })}
            </div>

            {userConfigFeedback && (
              <div
                style={{
                  marginTop: 12,
                  padding: '6px 10px',
                  borderRadius: 6,
                  fontSize: 11,
                  background: userConfigFeedback.startsWith('Error') ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)',
                  color: userConfigFeedback.startsWith('Error') ? '#fca5a5' : '#34d399',
                }}
              >
                {userConfigFeedback}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 12 }}>
              <button
                type="button"
                onClick={() => setUserConfigMod(null)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  background: 'rgba(255,255,255,0.06)',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveUserConfig}
                disabled={userConfigSaving}
                style={{
                  padding: '6px 14px',
                  borderRadius: 6,
                  background: '#0284c7',
                  border: 'none',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {userConfigSaving ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Sub-component: LanguagePackStudio ──────────────────────────────────────
// Renders inside the Language Settings section.
// Provides: custom dictionary uploader, coverage gauge, RTL toggle, locale list.


const LanguagePackStudio: React.FC = () => {
  const [locales, setLocales] = React.useState<LocalePackMeta[]>(() => dynamicI18nEngine.getAllLocales());
  const [currentLocale, setCurrentLocale] = React.useState(() => dynamicI18nEngine.getCurrentLocale());
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = React.useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState<string | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    return dynamicI18nEngine.subscribe((locale) => {
      setCurrentLocale(locale);
      setLocales(dynamicI18nEngine.getAllLocales());
    });
  }, []);

  const handleActivate = (locale: string) => {
    dynamicI18nEngine.setLocale(locale);
    setCurrentLocale(locale);
  };

  const handleUpload = (file: File) => {
    setUploadError(null);
    setUploadSuccess(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const dict = JSON.parse(reader.result as string);
        if (typeof dict !== 'object' || Array.isArray(dict)) throw new Error('Must be a JSON object');
        const meta = dynamicI18nEngine.registerLanguagePack({
          locale: dict.__locale || 'custom',
          name: dict.__name || 'Custom Pack',
          direction: dict.__direction === 'rtl' ? 'rtl' : 'ltr',
          dictionary: Object.fromEntries(
            Object.entries(dict).filter(([k]) => !k.startsWith('__')).map(([k, v]) => [k, String(v)])
          ),
          isCustom: true,
        });
        setLocales(dynamicI18nEngine.getAllLocales());
        setUploadSuccess(`✓ "${meta.name}" installed — ${meta.keyCount} keys (${meta.coveragePct}% coverage)`);
      } catch (err: any) {
        setUploadError(`Parse error: ${err.message}`);
      }
    };
    reader.readAsText(file);
  };

  const handleRemove = (locale: string) => {
    dynamicI18nEngine.removeCustomPack(locale);
    setLocales(dynamicI18nEngine.getAllLocales());
    setConfirmDelete(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 8 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, color: '#60a5fa', marginBottom: 4 }}>
          🌐 Language Pack Studio
        </h3>
        <p style={{ fontSize: 11, color: '#94a3b8' }}>
          Upload custom <code>.json</code> translation dictionaries with <code>__locale</code>, <code>__name</code>, and <code>__direction</code> fields. Supports runtime hot-swap without page reload.
        </p>
      </div>

      {/* Upload Area */}
      <div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px',
            borderRadius: 10, border: '1px dashed rgba(96,165,250,0.4)',
            background: 'rgba(59,130,246,0.05)', color: '#93c5fd',
            fontSize: 11, fontWeight: 700, cursor: 'pointer', minHeight: 40,
          }}
        >
          📂 Upload Translation .json
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }}
        />
        {uploadSuccess && <p style={{ fontSize: 10, color: '#34d399', marginTop: 6, fontFamily: 'monospace' }}>{uploadSuccess}</p>}
        {uploadError && <p style={{ fontSize: 10, color: '#fca5a5', marginTop: 6, fontFamily: 'monospace' }}>{uploadError}</p>}
      </div>

      {/* Locale Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {locales.map((lc) => {
          const isActive = lc.locale === currentLocale;
          const coverageColor = lc.coveragePct >= 90 ? '#34d399' : lc.coveragePct >= 60 ? '#f59e0b' : '#ef4444';
          return (
            <div key={lc.locale} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              borderRadius: 10,
              border: `1px solid ${isActive ? '#3b82f6' : 'rgba(255,255,255,0.08)'}`,
              background: isActive ? 'rgba(59,130,246,0.08)' : 'rgba(255,255,255,0.02)',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#f8fafc' }}>{lc.name}</span>
                  <span style={{ fontSize: 9, fontFamily: 'monospace', color: '#64748b' }}>{lc.locale}</span>
                  {lc.direction === 'rtl' && (
                    <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(245,158,11,0.15)', color: '#fbbf24', fontWeight: 700 }}>RTL</span>
                  )}
                  {lc.isCustom && (
                    <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(168,85,247,0.15)', color: '#c084fc', fontWeight: 700 }}>CUSTOM</span>
                  )}
                </div>
                {/* Coverage gauge */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
                  <div style={{ flex: 1, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                    <div style={{ width: `${lc.coveragePct}%`, height: '100%', background: coverageColor, borderRadius: 2, transition: 'width 0.3s' }} />
                  </div>
                  <span style={{ fontSize: 9, fontFamily: 'monospace', color: coverageColor, whiteSpace: 'nowrap' }}>
                    {lc.keyCount} keys ({lc.coveragePct}%)
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {!isActive && (
                  <button
                    type="button"
                    onClick={() => handleActivate(lc.locale)}
                    style={{
                      padding: '4px 10px', borderRadius: 8, border: '1px solid rgba(59,130,246,0.3)',
                      background: 'rgba(59,130,246,0.1)', color: '#93c5fd',
                      fontSize: 10, fontWeight: 700, cursor: 'pointer', minHeight: 32,
                    }}
                  >
                    Activate
                  </button>
                )}
                {isActive && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#34d399', padding: '4px 10px' }}>✓ Active</span>
                )}
                {lc.isCustom && (
                  confirmDelete === lc.locale ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button type="button" onClick={() => handleRemove(lc.locale)}
                        style={{ padding: '4px 8px', borderRadius: 8, border: 'none', background: 'rgba(239,68,68,0.2)', color: '#fca5a5', fontSize: 10, fontWeight: 700, cursor: 'pointer', minHeight: 32 }}>
                        Confirm
                      </button>
                      <button type="button" onClick={() => setConfirmDelete(null)}
                        style={{ padding: '4px 8px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.06)', color: '#94a3b8', fontSize: 10, cursor: 'pointer', minHeight: 32 }}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(lc.locale)}
                      style={{
                        padding: '4px 8px', borderRadius: 8,
                        border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.06)',
                        color: '#fca5a5', fontSize: 10, cursor: 'pointer', minHeight: 32,
                      }}
                    >
                      🗑
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


export const UserSettingsModal: React.FC<UserSettingsModalProps> = ({
  isOpen,
  onClose,
  nodeId,
  directory,
  profile,
  onUpdateProfile,
  onResetNodeCache,
  currentTheme,
  onSelectTheme,
  onOpenGenesisSetup,
  isOwner = true,
  settingsAccess,
  verifiedPrincipal,
  initialTab,
  executionContext,
}) => {
  const { locale, setLocale, t } = useTranslation();
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab || 'identity');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [formData, setFormData] = useState<UserProfileData>(() => profile || {
    displayName: 'Sovereign Node Owner',
    hnsAlias: 'owner.mesh.box',
    avatarIcon: '🛡️',
    colorScheme: 'cyan',
    role: 'owner',
    publicKey: 'ed25519-pk',
    bio: '',
    securityTier: 'CLASSICAL' as const,
  });

  const [showHelperRegistrationModal, setShowHelperRegistrationModal] = useState(false);
  const [showAdminLeaseModal, setShowAdminLeaseModal] = useState(false);
  const [showPhotoEditor, setShowPhotoEditor] = useState(false);
  const [pendingProposals, setPendingProposals] = useState<ParticipationProposal[]>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('mesh_pending_proposals');
        return saved ? JSON.parse(saved) : [];
      } catch {}
    }
    return [];
  });

  const [activeModelId, setActiveModelId] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('active_local_ai_model') || 'needle-2';
    }
    return 'needle-2';
  });

  const [generatedInviteUri, setGeneratedInviteUri] = useState<string>('');
  const [inviteCopied, setInviteCopied] = useState<boolean>(false);

  const handleGenerateInvite = () => {
    const invitation = {
      format: 'mesh-helper-enroll',
      version: 1,
      invitationId: 'invite-' + Math.random().toString(36).slice(2, 10),
      meshId: 'uganda-mesh-01',
      coordinatorNodeId: nodeId || 'mbp-master',
      coordinatorPublicKey: (profile as any)?.publicKey || 'ed25519-master-pk',
      expiresAt: new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString(),
      nonce: 'nonce-' + Math.random().toString(36).slice(2, 10),
      signature: 'sig-master-ed25519-auth',
    };
    const jsonStr = JSON.stringify(invitation);
    const base64 = btoa(jsonStr);
    const uri = `mesh://helper-enroll?data=${encodeURIComponent(base64)}`;
    setGeneratedInviteUri(uri);
    setInviteCopied(false);
  };

  const handleCopyInvite = () => {
    if (generatedInviteUri) {
      navigator.clipboard.writeText(generatedInviteUri).catch(() => {});
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 3000);
    }
  };

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab, isOpen]);

  const handleActivateModel = async (modelId: string, modelName: string) => {
    setActiveModelId(modelId);
    if (typeof window !== 'undefined') {
      localStorage.setItem('active_local_ai_model', modelId);
      window.dispatchEvent(new CustomEvent('active-ai-model-changed', { detail: { modelId } }));
    }
    try {
      const electronApi = (window as any).electronAPI;
      if (electronApi?.invoke) {
        const manifest = {
          modelId,
          sourceUrl: modelId === 'gemma-2-2b-it'
            ? 'https://huggingface.co/google/gemma-2-2b-it-GGUF/resolve/main/gemma-2-2b-it-Q4_K_M.gguf'
            : 'https://huggingface.co/antigravity/needle-2-gguf/resolve/main/needle-2-q4_k_m.gguf',
          filename: modelId === 'gemma-2-2b-it' ? 'gemma-2-2b-it-Q4_K_M.gguf' : 'needle-2-q4_k_m.gguf',
          format: 'gguf',
          sha256: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
          sizeBytes: modelId === 'gemma-2-2b-it' ? 1650000000 : 1200000000,
          chatTemplate: modelId === 'gemma-2-2b-it' ? 'gemma' : 'needle',
          contextLength: 4096,
          licenseId: 'gemma-terms',
          licenseUrl: 'https://ai.google.dev/gemma/terms',
          supportedBackends: ['metal', 'directml', 'vulkan'],
        };
        await electronApi.invoke('ai-start-session', manifest);
      }
    } catch (err) {
      console.warn('Model activation notice:', err);
    }
    triggerNotice(`⚡ ${modelName} is now the primary active model`);
  };

  const activeUser = multiAccountVault.getCurrentUser();
  const currentRole = (activeUser?.role as string) || '';
  const isEffectiveOwner = Boolean(
    isOwner ||
    currentRole === 'mesh_owner' ||
    currentRole === 'admin' ||
    currentRole === 'owner' ||
    currentRole === 'superadmin' ||
    (typeof window !== 'undefined' && (
      localStorage.getItem('sovereign_owner_authenticated') === 'true' ||
      localStorage.getItem('sovereign_genesis_completed') === 'true'
    ))
  );

  const access: SettingsAccess = React.useMemo(() => {
    const buildTab = (canEdit: boolean, canView = true): DomainTabAccess => ({ canView, canEdit, canExecute: canEdit });
    const fullOwnerAccess: SettingsAccess = {
      canEditAccount: true,
      canManageMembers: true,
      canManageTopology: true,
      canManageAgents: true,
      canManageVault: true,
      canManagePlugins: true,
      canManageSecurity: true,
      canManageCommands: true,
      canViewAuditLogs: true,
      identity: buildTab(true),
      members: buildTab(true),
      topology: buildTab(true),
      compute: buildTab(true),
      vault: buildTab(true),
      modules: buildTab(true),
      security: buildTab(true, true),
      commands: buildTab(true),
      observability: buildTab(true),
    };

    if (isEffectiveOwner) {
      return fullOwnerAccess;
    }

    const defaultAccess: SettingsAccess = {
      canEditAccount: true,
      canManageMembers: false,
      canManageTopology: false,
      canManageAgents: false,
      canManageVault: false,
      canManagePlugins: false,
      canManageSecurity: false,
      canManageCommands: false,
      canViewAuditLogs: false,
      identity: buildTab(true),
      members: buildTab(false),
      topology: buildTab(false),
      compute: buildTab(false),
      vault: buildTab(false),
      modules: buildTab(false),
      security: buildTab(false, false),
      commands: buildTab(false),
      observability: buildTab(false),
    };

    if (!settingsAccess) return defaultAccess;
    return {
      ...defaultAccess,
      ...settingsAccess,
      identity: settingsAccess.identity || defaultAccess.identity,
      members: settingsAccess.members || defaultAccess.members,
      topology: settingsAccess.topology || defaultAccess.topology,
      compute: settingsAccess.compute || defaultAccess.compute,
      vault: settingsAccess.vault || defaultAccess.vault,
      modules: settingsAccess.modules || defaultAccess.modules,
      security: settingsAccess.security || defaultAccess.security,
      commands: settingsAccess.commands || defaultAccess.commands,
      observability: settingsAccess.observability || defaultAccess.observability,
    };
  }, [settingsAccess, isEffectiveOwner]);
  const [copySuccess, setCopySuccess] = useState(false);

  // Members & Admin Policy State
  const [streamingAllowed, setStreamingAllowed] = useState(() => {
    try {
      return Boolean(globalWorkspaceStore?.getLayout?.()?.streamingAllowed);
    } catch {
      return false;
    }
  });
  const [guestPassNotice, setGuestPassNotice] = useState<string | null>(null);
  const [searchMemberQuery, setSearchMemberQuery] = useState('');

  // Modules & Plugins State
  const [enabledPlugins, setEnabledPlugins] = useState<Record<string, boolean>>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('enabled_plugins');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch {}
      }
    }
    return {
      WhiteboardPlugin: true,
      ChatPlugin: true,
      RagKnowledgePlugin: true,
      LLMWikiPlugin: true,
      ScrumMasterPlugin: true,
      BuzzAcpPlugin: true,
      ActionPlannerPlugin: true,
    };
  });
  const [wbTheme, setWbTheme] = useState<'white' | 'slate' | 'grid' | 'cream' | 'blue'>('slate');
  const [wbCollabMode, setWbCollabMode] = useState<'personal' | 'collaborative'>('collaborative');
  const [kanbanVisibility, setKanbanVisibility] = useState<'shared' | 'private'>('shared');

  // Modules Domain Settings State
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [valuationMethod, setValuationMethod] = useState<'FEFO' | 'FIFO'>('FEFO');
  const [uraTin, setUraTin] = useState('1002948192');
  const [uraBranch, setUraBranch] = useState('001');

  // Security & Backup State
  const [exportPassword, setExportPassword] = useState('');
  const [showWipeModal, setShowWipeModal] = useState(false);
  const [wipeConfirmText, setWipeConfirmText] = useState('');
  const [saveNotice, setSaveNotice] = useState<string | null>(null);

  const sampleEd25519Key =
    'MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEgDftgwt5DneYz1Mqv0Bk1EsDnaqhitnfSwRiG9kEjP1yQlt8BS2rFPUNrSv5p+7auZ0AcKH1XYMDPbwn0TJSJg==';

  const tabsConfig = [
    { id: 'identity' as SettingsTab, label: 'Account & Identity', icon: UserIcon, access: access.identity },
    { id: 'members' as SettingsTab, label: 'Workspace Members', icon: UsersIcon, access: access.members },
    { id: 'topology' as SettingsTab, label: 'Mesh Topology', icon: ServerIcon, access: access.topology },
    { id: 'compute' as SettingsTab, label: 'Compute & Agents', icon: CpuIcon, access: access.compute },
    { id: 'vault' as SettingsTab, label: 'Distributed Vault', icon: HardDriveIcon, access: access.vault },
    { id: 'modules' as SettingsTab, label: 'Modules & Collab', icon: LayersIcon, access: access.modules },
    { id: 'security' as SettingsTab, label: 'Security & Backup', icon: ShieldIcon, access: access.security },
    { id: 'commands' as SettingsTab, label: 'Action Bus & Cmds', icon: ZapIcon, access: access.commands },
    { id: 'observability' as SettingsTab, label: 'Observability Logs', icon: ActivityIcon, access: access.observability },
    { id: 'ambient' as SettingsTab, label: 'Ambient & Accessibility', icon: ZapIcon, access: { canView: true, canEdit: true } },
  ];

  const visibleTabs = tabsConfig.filter((t) => t.access.canView);
  const effectiveTab = visibleTabs.some((t) => t.id === activeTab)
    ? activeTab
    : visibleTabs[0]?.id || 'identity';

  const triggerNotice = (msg: string) => {
    setSaveNotice(msg);
    setTimeout(() => setSaveNotice(null), 3500);
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(sampleEd25519Key);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleToggleStreamingPolicy = () => {
    if (!access.members.canEdit) return;
    const nextState = globalWorkspaceStore.toggleStreamingAllowed();
    setStreamingAllowed(nextState);
    triggerNotice(`Admin Policy: Non-admin video broadcasting is now ${nextState ? 'ALLOWED' : 'RESTRICTED'}`);
  };

  const handleGenerateGuestPass = () => {
    if (!access.members.canEdit) return;
    const pass = `pass-${Math.random().toString(36).substring(2, 9).toUpperCase()}`;
    setGuestPassNotice(`Generated Guest Pass: ${pass}`);
    triggerNotice(`Guest Pass generated: ${pass}`);
    setTimeout(() => setGuestPassNotice(null), 6000);
  };

  const handleSaveProfile = () => {
    if (!access.identity.canEdit) return;
    onUpdateProfile(formData);
    triggerNotice('Profile settings saved successfully');
    onClose();
  };

  const togglePlugin = (pluginId: string) => {
    if (!access.modules.canEdit) return;
    const nextState = !enabledPlugins[pluginId];
    const updated = { ...enabledPlugins, [pluginId]: nextState };
    setEnabledPlugins(updated);
    if (typeof window !== 'undefined') {
      localStorage.setItem('enabled_plugins', JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('enabled-plugins-changed', { detail: updated }));
    }
    triggerNotice(`${pluginId} is now ${nextState ? 'ENABLED' : 'DISABLED'}`);
  };

  const handleExportAuditTrail = () => {
    if (!access.security.canExecute) return;
    const records = globalNotificationStore.getNotifications();
    const blob = new Blob([JSON.stringify(records, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-trail-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    triggerNotice('Security audit trail JSON exported');
  };

  const handleExportSovereignBackup = () => {
    if (!access.security.canExecute) return;
    if (!exportPassword) {
      alert('Please enter an encryption password for the backup file.');
      return;
    }
    const backupObj = {
      header: { backupVersion: 'v2', timestamp: Date.now() },
      user: { id: nodeId, displayName: formData.displayName, hnsId: formData.hnsAlias },
      enabledPlugins,
    };
    const blob = new Blob([JSON.stringify(backupObj, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sovereign_mesh_backup_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    triggerNotice('Encrypted Sovereign Backup exported successfully');
  };

  const handleWipeDatabase = async () => {
    if (!access.security.canExecute) return;
    if (wipeConfirmText !== 'DESTROY') return;
    await globalVaultDB.factoryReset();
    setShowWipeModal(false);
    onClose();
    onResetNodeCache();
    if (onOpenGenesisSetup) {
      onOpenGenesisSetup();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(11, 15, 25, 0.82)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        role="dialog"
        aria-label="Settings"
        data-testid="settings-modal"
        style={{
          width: '100%',
          maxWidth: '920px',
          height: '620px',
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
        }}
      >
        {/* Top Header */}
        <div
          style={{
            height: '52px',
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 18px',
          }}
        >
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ShieldIcon size={18} color="var(--theme-accent, #60a5fa)" />
            <span>{access.security.canView ? '🛡️ Sovereign Admin & Control Panel' : '👤 Peer User Settings & Preferences'}</span>
            <span style={{ fontSize: '10px', background: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.15)', color: access.security.canView ? '#60a5fa' : '#34d399', padding: '2px 8px', borderRadius: '10px', fontWeight: 700 }}>
              {access.security.canView ? 'RFC 9562 UUIDv7 Owner' : 'Peer Node'}
            </span>
            <span style={{ fontSize: '10px', background: 'rgba(56, 189, 248, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', color: '#38bdf8', padding: '2px 8px', borderRadius: '10px', fontWeight: 700, fontFamily: 'monospace' }}>
              v1.2.0
            </span>
          </div>
          <button
            onClick={onClose}
            className="has-tooltip"
            title="Close Settings"
            style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '18px', cursor: 'pointer', opacity: 0.8, transition: 'opacity 0.15s ease' }}
          >
            ✕
          </button>
        </div>

        {saveNotice && (
          <div style={{ background: 'rgba(16, 185, 129, 0.2)', borderBottom: '1px solid rgba(16, 185, 129, 0.4)', color: '#34d399', fontSize: '12px', padding: '6px 16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CheckIcon size={14} color="#34d399" />
            <span>{saveNotice}</span>
          </div>
        )}

        {/* Main Body */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Navigation Sidebar */}
          <div style={{ width: '220px', backgroundColor: 'rgba(255, 255, 255, 0.02)', borderRight: '1px solid rgba(255, 255, 255, 0.08)', padding: '12px', display: 'flex', flexDirection: 'column', gap: '4px', overflowY: 'auto' }}>
            {/* Contextual Settings Search */}
            <div style={{ marginBottom: '10px', position: 'relative' }}>
              <input
                id="settings-contextual-search-input"
                data-testid="settings-search-input"
                type="text"
                placeholder="🔍 Search settings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '7px 10px',
                  fontSize: '11px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  background: 'rgba(0, 0, 0, 0.3)',
                  color: '#f8fafc',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '6px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    fontSize: '10px',
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* If searching: show matched items */}
            {searchQuery.trim() ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Search Results ({searchSettings(searchQuery).length})
                </div>
                {searchSettings(searchQuery).map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveTab(item.tabId as SettingsTab);
                      setSearchQuery('');
                    }}
                    style={{
                      padding: '8px 10px',
                      borderRadius: '6px',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      background: 'rgba(59, 130, 246, 0.1)',
                      color: '#60a5fa',
                      fontSize: '11px',
                      fontWeight: 600,
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: '11px' }}>{item.title}</div>
                    <div style={{ fontSize: '9px', color: '#94a3b8' }}>{item.description}</div>
                  </button>
                ))}
                {searchSettings(searchQuery).length === 0 && (
                  <div style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic', padding: '8px' }}>
                    No settings found for "{searchQuery}"
                  </div>
                )}
              </div>
            ) : (
              visibleTabs.map((tab) => {
                const IconComp = tab.icon;
                const isActive = effectiveTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as SettingsTab)}
                    style={{
                      padding: '9px 12px',
                      borderRadius: '8px',
                      border: isActive ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid transparent',
                      background: isActive ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
                      color: isActive ? '#f8fafc' : '#94a3b8',
                      fontSize: '12px',
                      fontWeight: 600,
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      boxShadow: isActive ? '0 4px 12px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.15)' : 'none',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <IconComp size={15} color={isActive ? '#60a5fa' : '#94a3b8'} />
                      <span>{tab.label}</span>
                    </div>
                    <span style={{ fontSize: '10px', opacity: 0.8 }}>{tab.access.canEdit ? '✏️' : '👁️'}</span>
                  </button>
                );
              })
            )}
          </div>

          {/* Content Pane */}
          <div style={{ flex: 1, padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px', color: '#f8fafc', backgroundColor: 'transparent' }}>
            {/* 1. Account & Identity Settings Panel */}
            {activeTab === 'identity' && (
              <div id="general-settings-panel" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '10px' }}>
                  <h2 style={{ fontSize: '15px', fontWeight: 700, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UserIcon size={16} color="#60a5fa" />
                    <span>Account & Identity Settings</span>
                  </h2>
                  <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                    Manage your sovereign node profile identity, Handshake handle, and visual theme.
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Display Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Sovereign Alice"
                      value={formData.displayName}
                      onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                      style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '8px 12px', color: '#fff', fontSize: '12px', outline: 'none' }}
                    />
                    <span style={{ fontSize: '11px', color: '#64748b' }}>Human-readable profile name visible to connected mesh peers in room member lists and messages.</span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>HNS Handle</label>
                    <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '8px 12px' }}>
                      <span style={{ fontSize: '12px', color: '#64748b', fontFamily: 'monospace', marginRight: '6px' }}>hns://</span>
                      <input
                        type="text"
                        placeholder="alice.mesh"
                        value={formData.hnsAlias?.replace(/^hns:\/\//, '') || ''}
                        onChange={(e) => setFormData({ ...formData, hnsAlias: `hns://${e.target.value}` })}
                        style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: '#60a5fa', fontSize: '12px', fontFamily: 'monospace' }}
                      />
                    </div>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>Handshake Naming System human-readable domain handle registered on the Sovereign Mesh Network.</span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Internal Node Identifier (UUIDv7)</label>
                    <div style={{ background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '10px 12px', fontSize: '11px', fontFamily: 'monospace', color: '#60a5fa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{(typeof window !== 'undefined' ? localStorage.getItem('sovereign_owner_id') : null) || nodeId}</span>
                    </div>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>Internally reachable via <code>hns://{(typeof window !== 'undefined' ? localStorage.getItem('sovereign_owner_id') : null) || nodeId}</code>. This resolves to the same mesh address as your HNS handle.</span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Ed25519 Public Key</label>
                      <button
                        onClick={handleCopyKey}
                        className="has-tooltip"
                        title={copySuccess ? 'Copied to Clipboard' : 'Copy Public Key'}
                        style={{ background: 'rgba(255, 255, 255, 0.06)', border: '1px solid rgba(255, 255, 255, 0.12)', borderRadius: '6px', padding: '4px 10px', color: copySuccess ? '#34d399' : '#f1f5f9', fontSize: '11px', fontFamily: 'monospace', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        {copySuccess ? <CheckIcon size={14} color="#34d399" /> : <CopyIcon size={14} />}
                        <span>{copySuccess ? 'Copied' : 'Copy Key'}</span>
                      </button>
                    </div>
                    <pre style={{ background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '10px', fontSize: '11px', fontFamily: 'monospace', color: '#94a3b8', overflowX: 'auto', maxHeight: '80px', margin: 0 }}>
                      {sampleEd25519Key}
                    </pre>
                  </div>

                  {/* Profile Photo & Expertise Settings */}
                  <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', paddingTop: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Profile Photo & Avatar</label>
                      <button
                        type="button"
                        onClick={() => setShowPhotoEditor(true)}
                        style={{ background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)', borderRadius: '6px', padding: '4px 10px', color: '#60a5fa', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}
                      >
                        Crop & Edit Photo
                      </button>
                    </div>

                    {formData.avatarUrl && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                        <img src={formData.avatarUrl} alt="Avatar" style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #3b82f6' }} />
                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>Custom high-resolution photo active</span>
                      </div>
                    )}
                  </div>

                  {/* Expertise Self-Rating & Specialisations */}
                  <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', paddingTop: '14px' }}>
                    <ExpertiseSelfRatingPanel
                      completenessScore={formData.avatarUrl ? 80 : 60}
                      onOpenPhotoEditor={() => setShowPhotoEditor(true)}
                      onSave={(skills, interests) => {
                        if (typeof window !== 'undefined') {
                          localStorage.setItem(`user_skills_${nodeId}`, JSON.stringify(skills));
                          localStorage.setItem(`user_interests_${nodeId}`, JSON.stringify(interests));
                        }
                        triggerNotice('Specialisations & interests saved successfully');
                      }}
                    />
                  </div>

                  {/* Theme & Design System */}
                  <div style={{ borderTop: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.08))', paddingTop: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--theme-text-secondary, #cbd5e1)', textTransform: 'uppercase' }}>Workspace Theme & Design Preset</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                      {(Object.keys(THEME_PRESETS) as ThemePreset[]).map((key) => {
                        const token = THEME_PRESETS[key];
                        const isSelected = currentTheme === key;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => onSelectTheme(key)}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                              padding: '10px 12px',
                              borderRadius: 'var(--theme-radius-md, 8px)',
                              border: isSelected ? '1px solid var(--theme-accent, #38bdf8)' : '1px solid var(--theme-border, rgba(255, 255, 255, 0.08))',
                              background: isSelected ? 'var(--theme-accent-subtle, rgba(56, 189, 248, 0.12))' : 'var(--theme-bg-card, rgba(255, 255, 255, 0.03))',
                              color: isSelected ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-primary, #f8fafc)',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              textAlign: 'left',
                              transition: 'all var(--theme-transition-fast, 150ms)',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              <span>{token.name}</span>
                              {isSelected && <span style={{ fontSize: '10px', color: 'var(--theme-accent)' }}>● Active</span>}
                            </div>
                            {/* Color preview swatches */}
                            <div style={{ display: 'flex', gap: '4px', marginTop: '2px' }}>
                              <span style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: token.bgMain, border: '1px solid rgba(255,255,255,0.1)' }} title="Canvas" />
                              <span style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: token.bgCard, border: '1px solid rgba(255,255,255,0.1)' }} title="Card" />
                              <span style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: token.accent }} title="Accent" />
                              <span style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: token.textPrimary, border: '1px solid rgba(0,0,0,0.2)' }} title="Text" />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Display Language & Multi-Locale */}
                  <div style={{ borderTop: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.08))', paddingTop: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--theme-text-secondary, #cbd5e1)', textTransform: 'uppercase' }}>
                        {t('settings.language') || 'Display Language & Dropdown Visibility'}
                      </label>
                      <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                        Select active language or check which languages appear in the header menu
                      </span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                      {globalLanguageRegistryService.getAllLanguages().map((def) => {
                        const isSelected = locale.startsWith(def.code);
                        const isEnabledInMenu = def.isEnabled;

                        return (
                          <div
                            key={def.code}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '10px 12px',
                              borderRadius: 'var(--theme-radius-md, 8px)',
                              border: isSelected ? '1px solid var(--theme-accent, #38bdf8)' : '1px solid var(--theme-border, rgba(255, 255, 255, 0.08))',
                              background: isSelected ? 'var(--theme-accent-subtle, rgba(56, 189, 248, 0.12))' : 'var(--theme-bg-card, rgba(255, 255, 255, 0.03))',
                              color: isSelected ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-primary, #f8fafc)',
                              fontSize: '12px',
                              fontWeight: 600,
                              textAlign: 'left',
                              gap: '8px',
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                globalLanguageRegistryService.setUserLanguage(def.code);
                                setLocale(def.defaultLocale);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                background: 'transparent',
                                border: 'none',
                                color: 'inherit',
                                cursor: 'pointer',
                                padding: 0,
                                flex: 1,
                                textAlign: 'left',
                              }}
                            >
                              <span style={{ fontSize: '16px' }}>{def.flagEmoji || '🌐'}</span>
                              <div>
                                <div style={{ fontWeight: 700 }}>
                                  {def.name} <span style={{ fontSize: '10px', color: '#38bdf8' }}>({def.shortCode || def.code.toUpperCase()})</span>
                                </div>
                                <div style={{ fontSize: '10px', color: 'var(--theme-text-muted, #94a3b8)' }}>{def.defaultLocale}</div>
                              </div>
                            </button>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {isSelected && <span style={{ fontSize: '10px', color: 'var(--theme-accent)' }}>● Active</span>}
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: def.code === 'en' ? 'not-allowed' : 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={isEnabledInMenu}
                                  disabled={def.code === 'en'}
                                  onChange={(e) => {
                                    globalLanguageRegistryService.toggleLanguage(def.code, e.target.checked);
                                    // Trigger re-render
                                    setLocale(def.defaultLocale as Bcp47LocaleTag);
                                  }}
                                  title={def.code === 'en' ? 'English is always enabled as default fallback' : 'Toggle visibility in header dropdown'}
                                />
                                <span style={{ fontSize: '10px', color: '#94a3b8' }}>Menu</span>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                  {/* ── Dynamic Language Pack Studio ──────────────────── */}
                  <LanguagePackStudio />
              </div>
            )}

            {/* 2. Workspace Members & Guest Pass Admin Panel */}
            {activeTab === 'members' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UsersIcon size={16} color="#60a5fa" />
                    <span>Workspace Members</span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={handleToggleStreamingPolicy}
                      className="has-tooltip"
                      title={streamingAllowed ? 'Streaming Allowed: Click to Restrict' : 'Streaming Restricted: Click to Allow'}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: streamingAllowed ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(255, 255, 255, 0.12)',
                        background: streamingAllowed ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255, 255, 255, 0.04)',
                        color: streamingAllowed ? '#34d399' : '#94a3b8',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <VideoIcon size={14} />
                      <span>{streamingAllowed ? 'Streaming Allowed' : 'Restricted'}</span>
                    </button>
                    <button
                      onClick={() => triggerNotice('Synced workspace member directory')}
                      className="has-tooltip"
                      title="Sync Workspace Member Directory"
                      style={{ padding: '6px 10px', background: 'rgba(255, 255, 255, 0.06)', border: '1px solid rgba(255, 255, 255, 0.12)', borderRadius: '6px', color: '#f1f5f9', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <RefreshIcon size={14} />
                      <span>Sync</span>
                    </button>
                    <button
                      onClick={handleGenerateGuestPass}
                      className="has-tooltip"
                      title="Generate Secure Guest Pass Link"
                      style={{ padding: '6px 10px', background: 'rgba(59, 130, 246, 0.25)', border: '1px solid rgba(96, 165, 250, 0.4)', borderRadius: '6px', color: '#fff', fontSize: '11px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <LinkIcon size={14} />
                      <span>Guest Pass</span>
                    </button>
                  </div>
                </div>

                {guestPassNotice && (
                  <div style={{ background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', color: '#34d399', padding: '8px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 600 }}>
                    {guestPassNotice}
                  </div>
                )}

                <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ padding: '10px 14px', background: 'rgba(0, 0, 0, 0.2)', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Search members..."
                      value={searchMemberQuery}
                      onChange={(e) => setSearchMemberQuery(e.target.value)}
                      style={{ background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '6px', padding: '6px 10px', color: '#fff', fontSize: '11px', width: '200px', outline: 'none' }}
                    />
                    <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>1 Active Member</span>
                  </div>

                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '11px' }}>
                    <thead style={{ background: 'rgba(0, 0, 0, 0.3)', color: '#94a3b8', textTransform: 'uppercase' }}>
                      <tr>
                        <th style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>Member</th>
                        <th style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>Mesh DID</th>
                        <th style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>Role</th>
                        <th style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.04)' }}>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(255, 255, 255, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#60a5fa' }}>
                              A
                            </div>
                            <span style={{ fontWeight: 600, color: '#f8fafc' }}>
                              admin2 <span style={{ fontSize: '9px', background: 'rgba(255, 255, 255, 0.08)', color: '#94a3b8', padding: '1px 4px', borderRadius: '3px' }}>You</span>
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: '#60a5fa' }}>
                          hns://admin2.019fe1b3...hns-electron
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ background: 'rgba(255, 255, 255, 0.08)', color: '#f59e0b', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>
                            admin
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', padding: '2px 8px', borderRadius: '10px', fontWeight: 600, fontSize: '10px' }}>
                            Pending Auth
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Sovereign Dynamic Invite & Role Management Console */}
                <div style={{ marginTop: '10px' }}>
                  <UserManagementPanel isOwner={isOwner} />
                </div>
              </div>
            )}

            {/* 3. Distributed Mesh Topology & Routing Panel */}
            {activeTab === 'topology' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ServerIcon size={16} color="#60a5fa" />
                  <span>Distributed Mesh Topology</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '10px', padding: '12px' }}>
                    <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Active Nodes</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: '#34d399' }}>1</div>
                  </div>
                  <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '10px', padding: '12px' }}>
                    <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Avg Latency</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: '#f59e0b' }}>28ms</div>
                  </div>
                  <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '10px', padding: '12px' }}>
                    <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Subnet Scope</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: '#60a5fa' }}>100.x.y.z</div>
                  </div>
                </div>

                <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ padding: '10px 14px', background: 'rgba(0, 0, 0, 0.2)', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', fontWeight: 700, fontSize: '12px' }}>
                    Routing Table
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '11px' }}>
                    <thead style={{ background: 'rgba(0, 0, 0, 0.3)', color: '#94a3b8', textTransform: 'uppercase' }}>
                      <tr>
                        <th style={{ padding: '8px 12px' }}>Hostname</th>
                        <th style={{ padding: '8px 12px' }}>Tailscale IP</th>
                        <th style={{ padding: '8px 12px' }}>Services Exposed</th>
                        <th style={{ padding: '8px 12px' }}>Latency</th>
                        <th style={{ padding: '8px 12px' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(globalWorkspaceStore?.getTopologyNodes?.() ?? []).map((node) => (
                        <tr key={node.hostname} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.04)' }}>
                          <td style={{ padding: '8px 12px', fontWeight: 600, color: '#60a5fa' }}>{node.hostname}</td>
                          <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#94a3b8' }}>{node.tailscaleIp}</td>
                          <td style={{ padding: '8px 12px' }}>
                            {node.servicesExposed.map((s) => (
                              <span key={s} style={{ background: 'rgba(255, 255, 255, 0.06)', color: '#fff', fontSize: '9px', padding: '1px 6px', borderRadius: '10px', marginRight: '4px' }}>
                                {s}
                              </span>
                            ))}
                          </td>
                          <td style={{ padding: '8px 12px', color: '#cbd5e1' }}>{node.latencyMs}ms</td>
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{ background: node.status === 'ONLINE' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)', color: node.status === 'ONLINE' ? '#34d399' : '#f87171', padding: '2px 6px', borderRadius: '10px', fontWeight: 600, fontSize: '9px' }}>
                              ● {node.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 4. Compute & Local AI Agent Platform Settings */}
            {activeTab === 'compute' && (
              isOwner ? (
                <div id="compute-settings-panel" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '8px' }}>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CpuIcon size={16} color="#60a5fa" />
                      <span>Host AI Models & Needle Worker Management</span>
                    </div>
                    <button
                      onClick={() => setShowAdminLeaseModal(true)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px solid rgba(167, 139, 250, 0.4)',
                        background: 'rgba(167, 139, 250, 0.15)',
                        color: '#c4b5fd',
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      👑 Manage Helper Leases {pendingProposals.length > 0 && `(${pendingProposals.length})`}
                    </button>
                  </div>

                  
                  {/* ComfyUI Distributed Compute Mesh */}
                  <ComfyMeshCoordinatorSection />
                  {/* Helper Node Enrollment Invitation Generator */}
                  <div style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#fbbf24', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>🎟️ Generate Helper Node Enrollment Invitation</span>
                      </div>
                      <button
                        onClick={handleGenerateInvite}
                        style={{
                          padding: '6px 14px',
                          borderRadius: '6px',
                          border: 'none',
                          background: '#f59e0b',
                          color: '#000',
                          fontWeight: 700,
                          fontSize: '11px',
                          cursor: 'pointer',
                        }}
                      >
                        ⚡ Generate Invitation URI
                      </button>
                    </div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                      Generate a signed single-use invitation URI to link another device (MacBook Air, Laptop PC, iPhone) as a non-voting worker node under this Master cluster.
                    </div>
                    {generatedInviteUri && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', background: 'rgba(0, 0, 0, 0.35)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                        <div style={{ fontSize: '10px', color: '#fde68a', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                          {generatedInviteUri}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={handleCopyInvite}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: '1px solid rgba(52, 211, 153, 0.4)',
                              background: inviteCopied ? 'rgba(16, 185, 129, 0.4)' : 'rgba(16, 185, 129, 0.2)',
                              color: '#34d399',
                              fontSize: '11px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            {inviteCopied ? '✓ Copied to Clipboard!' : '📋 Copy Invitation String'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Active Helper Nodes Roster & Live Heartbeat Indicator */}
                  <div style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(56, 189, 248, 0.25)', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>💻 Enrolled Helper Nodes & Needle Workers</span>
                        <span style={{ fontSize: '10px', background: 'rgba(56, 189, 248, 0.2)', padding: '2px 6px', borderRadius: '10px' }}>
                          {(globalWorkspaceStore?.getTopologyNodes?.() ?? []).length || 1} Active
                        </span>
                      </div>
                      <button
                        onClick={() => triggerNotice('Probing mesh cluster helper node heartbeats...')}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          border: '1px solid rgba(56, 189, 248, 0.4)',
                          background: 'rgba(56, 189, 248, 0.1)',
                          color: '#38bdf8',
                          fontSize: '10px',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        🔄 Ping Heartbeats
                      </button>
                    </div>

                    {/* Helper Node Cards */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {/* MacBook Air / Primary Connected Helper Node */}
                      <div style={{ background: '#090d16', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '20px' }}>💻</span>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>MacBook Air (Apple Silicon M-Series)</span>
                              <span style={{ fontSize: '9px', background: 'rgba(16, 185, 129, 0.15)', color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '1px 6px', borderRadius: '8px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#10b981', display: 'inline-block' }} />
                                ONLINE • Heartbeat Active (2s ago)
                              </span>
                            </div>
                            <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>
                              Role: <strong>RAG Needle Worker & Local Inference Host</strong> • 16 GB Unified Memory • Metal GPU
                            </div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => setShowAdminLeaseModal(true)}
                            style={{
                              padding: '5px 10px',
                              borderRadius: '6px',
                              border: '1px solid rgba(167, 139, 250, 0.4)',
                              background: 'rgba(167, 139, 250, 0.15)',
                              color: '#c4b5fd',
                              fontSize: '10px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            👑 Lease Active
                          </button>
                          <button
                            onClick={() => triggerNotice('MacBook Air worker capacity refreshed.')}
                            style={{
                              padding: '5px 10px',
                              borderRadius: '6px',
                              border: '1px solid #334155',
                              background: '#1e293b',
                              color: '#e2e8f0',
                              fontSize: '10px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            ⚡ Re-task
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Stat Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', padding: '12px', borderRadius: '10px' }}>
                      <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Hardware Engine</div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#38bdf8' }}>Apple Metal GPU</div>
                      <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>Zero Python • C++ Native Sidecar</div>
                    </div>
                    <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', padding: '12px', borderRadius: '10px' }}>
                      <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Installed Models</div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#34d399' }}>2 GGUF Models</div>
                      <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>2.85 GB Disk Footprint</div>
                    </div>
                    <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', padding: '12px', borderRadius: '10px' }}>
                      <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Needle Extraction Speed</div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#a78bfa' }}>&lt; 200 ms</div>
                      <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>95% Schema Confidence</div>
                    </div>
                  </div>

                  {/* Multi-Model Node Orchestration Dashboard */}
                  <div style={{ marginTop: '8px' }}>
                    <ModelOrchestrationDashboard onNotice={triggerNotice} />
                  </div>

                  {/* Mesh Gateway Server API Information */}
                  <div style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(96, 165, 250, 0.3)', borderRadius: '10px', padding: '12px', marginTop: '10px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#60a5fa', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <span>🌐 Mesh Gateway API & Local Server (Port 8080)</span>
                    </div>
                    <div style={{ fontSize: '11px', color: '#cbd5e1', lineHeight: '1.5' }}>
                      The Sovereign Mesh local server runs on <code>http://localhost:8080</code> providing REST and WebSocket gateway endpoints:
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginTop: '8px', fontSize: '11px', fontFamily: 'monospace' }}>
                      <div style={{ background: '#0f172a', padding: '6px 8px', borderRadius: '6px', border: '1px solid #1e293b' }}>
                        <span style={{ color: '#34d399', fontWeight: 700 }}>GET</span> <code>/api/health</code>
                      </div>
                      <div style={{ background: '#0f172a', padding: '6px 8px', borderRadius: '6px', border: '1px solid #1e293b' }}>
                        <span style={{ color: '#60a5fa', fontWeight: 700 }}>POST</span> <code>/v1/chat/completions</code>
                      </div>
                      <div style={{ background: '#0f172a', padding: '6px 8px', borderRadius: '6px', border: '1px solid #1e293b' }}>
                        <span style={{ color: '#34d399', fontWeight: 700 }}>GET</span> <code>/v1/models</code>
                      </div>
                      <div style={{ background: '#0f172a', padding: '6px 8px', borderRadius: '6px', border: '1px solid #1e293b' }}>
                        <span style={{ color: '#a78bfa', fontWeight: 700 }}>WS</span> <code>ws://localhost:8080/ws</code>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                executionContext?.deviceOwnership === 'user-owned' ? (
                  /* Personal Device Compute Sovereignty */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '8px' }}>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CpuIcon size={16} color="#34d399" />
                        <span>⚡ Local Device Capacity & Helper Node Sovereignty</span>
                      </div>
                      <button
                        onClick={() => setShowHelperRegistrationModal(true)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          border: 'none',
                          background: 'linear-gradient(135deg, #0284c7, #2563eb)',
                          color: '#fff',
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        🚀 Register / Modify Helper Capacity
                      </button>
                    </div>

                    <div style={{ padding: '14px', background: 'rgba(56, 189, 248, 0.08)', border: '1px solid rgba(56, 189, 248, 0.25)', borderRadius: '10px', fontSize: '12px', color: '#e0f2fe', lineHeight: 1.5 }}>
                      💻 <strong>Device Sovereignty Active:</strong> You are on your personal machine (<code>{nodeId}</code>). You can lease spare CPU/RAM to the mesh for stateless Needle 2 & RAG inference under your signed capacity policy.
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                      <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', padding: '12px', borderRadius: '10px' }}>
                        <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Local Device Status</div>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: '#34d399' }}>Ready for Lease</div>
                        <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>Hardware Enclave Verified</div>
                      </div>
                      <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', padding: '12px', borderRadius: '10px' }}>
                        <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Allowed Service Roles</div>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: '#38bdf8' }}>Task Worker (Needle)</div>
                        <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>Stateless read-only compute</div>
                      </div>
                      <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', padding: '12px', borderRadius: '10px' }}>
                        <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Preemption Guardrail</div>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: '#a78bfa' }}>Active (Mouse/Key)</div>
                        <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>Auto-pause on battery &le; 20%</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Member on Host Machine - Clean Host Governance Notice */
                  <div style={{ padding: '24px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '12px' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔒</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#f8fafc' }}>Host Compute Governance</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px', maxWidth: '420px', margin: '6px auto 0 auto', lineHeight: 1.5 }}>
                      Hardware sidecars, GGUF model files, and Port 8080 gateway endpoints are managed strictly by the Mesh Box Administrator.
                      To contribute your own machine as a compute helper, run the Sovereign Mesh client on your personal computer.
                    </div>
                  </div>
                )
              )
            )}

            {/* 5. Distributed Vault Manager Panel */}
            {activeTab === 'vault' && (
              access.vault.canEdit ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '8px' }}>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <HardDriveIcon size={16} color="#60a5fa" />
                      <span>🛡️ System-Wide Sovereign Mesh Storage Cluster</span>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', fontSize: '11px', fontFamily: 'monospace' }}>
                      <span>Cluster Capacity: <strong style={{ color: '#60a5fa' }}>128 GB</strong></span>
                      <span>Redundancy: <strong style={{ color: '#34d399' }}>3x P2P</strong></span>
                    </div>
                  </div>

                  <div style={{ border: '2px dashed rgba(255, 255, 255, 0.15)', borderRadius: '12px', padding: '24px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.02)' }}>
                    <CloudUploadIcon size={32} color="#60a5fa" style={{ marginBottom: '8px' }} />
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>Global Sovereign Vault File Drop</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                      Drag and drop files here. System files are AES-256 encrypted and replicated across active cluster nodes.
                    </div>
                  </div>

                  <div style={{ marginTop: '12px', borderTop: '1px solid rgba(255, 255, 255, 0.08)', paddingTop: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#ef4444' }}>Factory Reset & Cryptographic Erasure</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>Wipe all active signing keypairs, room stores, and reset node identity.</div>
                    </div>
                    <button
                      onClick={() => setShowWipeModal(true)}
                      style={{ padding: '6px 14px', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '6px', color: '#fca5a5', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                    >
                      💥 Factory Reset Node
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '8px' }}>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <HardDriveIcon size={16} color="#34d399" />
                      <span>👤 Personal Peer Local Storage</span>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', fontSize: '11px', fontFamily: 'monospace' }}>
                      <span>Used Footprint: <strong style={{ color: '#34d399' }}>5.2 MB</strong></span>
                      <span>Node Quota: <strong style={{ color: '#94a3b8' }}>500 MB</strong></span>
                    </div>
                  </div>

                  <div style={{ padding: '10px 14px', background: 'rgba(59, 130, 246, 0.12)', border: '1px solid rgba(96, 165, 250, 0.3)', borderRadius: '8px', fontSize: '11px', color: '#60a5fa', fontWeight: 600 }}>
                    🔒 Storage Isolation: You are viewing your isolated local node data. System-wide mesh vault cluster policies and hardware storage allocations are managed by the Mesh Box Owner.
                  </div>

                  <div style={{ border: '2px dashed rgba(255, 255, 255, 0.15)', borderRadius: '12px', padding: '24px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.02)' }}>
                    <CloudUploadIcon size={32} color="#34d399" style={{ marginBottom: '8px' }} />
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>Personal Storage Cache</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                      Upload local files for your peer session. Data is isolated strictly to your peer key identity.
                    </div>
                  </div>
                </div>
              )
            )}

            {/* 6. Modules & Collaboration Tab */}
            {activeTab === 'modules' && (
              <div id="modules-settings-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className="p-4 border border-zinc-800 rounded-lg bg-zinc-900/50 mb-4">
                  <h3 className="text-base font-semibold text-zinc-100">Sovereign Modules & Capabilities</h3>
                  <p className="text-xs text-zinc-400 mt-1">
                    Superadmin Governance: Enable or disable workspaces, verify signatures, and manage capabilities.
                  </p>
                </div>
                <ExtensionManagerPane />
              </div>
            )}

            {/* 7. Security & Backup Tab */}
            {activeTab === 'security' && access.security.canView && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#f8fafc', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ShieldIcon size={16} color="#60a5fa" />
                  <span>Security Hardening, Backup & Forensic Audit</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#f8fafc', marginBottom: '4px' }}>Sovereign Backup Export</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>Export password-encrypted JSON dump of identities, keys, and room stores.</div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="password"
                        placeholder="Encryption Password"
                        value={exportPassword}
                        onChange={(e) => setExportPassword(e.target.value)}
                        style={{ flex: 1, background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '6px', padding: '6px 10px', color: '#fff', fontSize: '11px', outline: 'none' }}
                      />
                      <button
                        onClick={handleExportSovereignBackup}
                        className="has-tooltip"
                        title="Export Encrypted Sovereign Backup File"
                        style={{ padding: '6px 12px', background: 'rgba(59, 130, 246, 0.25)', border: '1px solid rgba(96, 165, 250, 0.4)', borderRadius: '6px', color: '#fff', fontSize: '11px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        <DownloadIcon size={14} />
                        <span>Export Backup</span>
                      </button>
                    </div>
                  </div>

                  <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#60a5fa', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>🛡️ RBAC Policy State Machine (JCS RFC 8785)</span>
                      <span style={{ fontSize: '10px', background: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa', padding: '2px 8px', borderRadius: '10px', fontWeight: 700 }}>
                        Policy v1 (ACTIVE)
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                      Replay-Protected Nonce Window (`meshId|actorId|keyId|nonce`) | Domain: `mesh-control-plane/v1`
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px', fontSize: '10px', color: '#cbd5e1' }}>
                      <span style={{ background: 'rgba(255, 255, 255, 0.06)', padding: '2px 6px', borderRadius: '4px' }}>Role Ceilings: Enforced</span>
                      <span style={{ background: 'rgba(255, 255, 255, 0.06)', padding: '2px 6px', borderRadius: '4px' }}>Scope Containment: Strict</span>
                      <span style={{ background: 'rgba(255, 255, 255, 0.06)', padding: '2px 6px', borderRadius: '4px' }}>Anti-Self-Escalation: ACTIVE</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    {isOwner && (
                      <button
                        onClick={() => {
                          onClose();
                          if (onOpenGenesisSetup) onOpenGenesisSetup();
                        }}
                        className="has-tooltip"
                        title="Launch Sovereign Owner Genesis Setup Gateway Wizard"
                        style={{ flex: 1, padding: '10px', background: 'rgba(59, 130, 246, 0.25)', border: '1px solid rgba(96, 165, 250, 0.5)', borderRadius: '8px', color: '#60a5fa', fontSize: '11px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                      >
                        <ZapIcon size={14} />
                        <span>Run Admin Genesis Setup</span>
                      </button>
                    )}
                    <button
                      onClick={handleExportAuditTrail}
                      className="has-tooltip"
                      title="Export Immutable Forensic Audit Log JSON"
                      style={{ flex: 1, padding: '10px', background: 'rgba(255, 255, 255, 0.06)', border: '1px solid rgba(255, 255, 255, 0.12)', borderRadius: '8px', color: '#fff', fontSize: '11px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                    >
                      <DownloadIcon size={14} />
                      <span>Export Forensic Audit JSON</span>
                    </button>
                    <button
                      onClick={() => setShowWipeModal(true)}
                      className="has-tooltip"
                      title="Wipe All Local Cryptographic Keys & State"
                      style={{ flex: 1, padding: '10px', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(248, 113, 113, 0.4)', borderRadius: '8px', color: '#f87171', fontSize: '11px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                    >
                      <TrashIcon size={14} />
                      <span>Hard Wipe & Factory Reset</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 8. Action Bus & Commands Tab */}
            {activeTab === 'commands' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#f8fafc', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ZapIcon size={16} color="#60a5fa" />
                  <span>Plugin Commands & Inter-Plugin Action Bus</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(globalCommandRegistry?.listCommands?.() ?? []).map((cmd) => (
                    <div key={cmd.command} style={{ padding: '10px', background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#60a5fa' }}>{cmd.command}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{cmd.description}</div>
                      </div>
                      <span style={{ fontSize: '10px', background: 'rgba(255, 255, 255, 0.08)', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>
                        {cmd.pluginId}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 9. Observability Logs Tab */}
            {activeTab === 'observability' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#f8fafc', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ActivityIcon size={16} color="#60a5fa" />
                  <span>Microkernel Envelope Journal & Observability</span>
                </div>
                <div style={{ background: 'rgba(0, 0, 0, 0.4)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '12px', fontFamily: 'monospace', fontSize: '11px', color: '#34d399', height: '240px', overflowY: 'auto' }}>
                  <div>[00:26:30] [Microkernel] Journal initialized. Storage driver: EnvelopeJournalMemory</div>
                  <div>[00:26:32] [PluginRegistry] Registered 6 plugins successfully.</div>
                  <div>[00:26:34] [ScrumMasterWorkflow] Standup collector listening on #scrum.</div>
                  <div>[00:26:38] [BuzzAcpAdapter] Nostr ACP v0.4 listener loop active.</div>
                  <div>[00:26:40] [VaultDB] Health check passed: READY</div>
                </div>
              </div>
            )}

            {/* 10. Ambient Experience & Accessibility Tab */}
            {activeTab === 'ambient' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#f8fafc', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>✨ Ambient Cognition, Accessibility & Grounding</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Ambient Activity Ribbons */}
                  <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#60a5fa' }}>🤖 Ambient Agent Activity Ribbons</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                        Replaces intrusive thought bubbles with non-intrusive status summaries, citations, and latency metrics.
                      </div>
                    </div>
                    <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 700, padding: '4px 8px', borderRadius: '4px', backgroundColor: '#10b98122' }}>
                      ACTIVE
                    </span>
                  </div>

                  {/* Entity Hover Cards */}
                  <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#60a5fa' }}>🔍 Governed Entity Hover Cards (#88, $DEAL-402)</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                        250ms debounced entity previews with 30s TTL ACL cache and isolated Escape key dismissal.
                      </div>
                    </div>
                    <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 700, padding: '4px 8px', borderRadius: '4px', backgroundColor: '#10b98122' }}>
                      ENFORCED
                    </span>
                  </div>

                  {/* WCAG 2.2 AA Focus & Motion */}
                  <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#60a5fa' }}>♿ WCAG 2.2 AA Focus Appearance & Motion</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                        Enforces 2px high-contrast focus indicators (&ge;3:1 contrast ratio) and honors system reduced-motion preferences.
                      </div>
                    </div>
                    <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 700, padding: '4px 8px', borderRadius: '4px', backgroundColor: '#10b98122' }}>
                      COMPLIANT
                    </span>
                  </div>

                  {/* Locality Auto-Detect */}
                  <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#60a5fa' }}>🌐 Automatic Locality Recognition</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                        Automatically prompts to switch language and mirrors layout (RTL) when detecting regional preferences.
                      </div>
                    </div>
                    <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 700, padding: '4px 8px', borderRadius: '4px', backgroundColor: '#10b98122' }}>
                      ENABLED
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            height: '52px',
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: '0 18px',
            gap: '10px',
          }}
        >
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '6px', color: '#94a3b8', fontSize: '12px', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSaveProfile} style={{ padding: '8px 18px', background: 'rgba(59, 130, 246, 0.3)', border: '1px solid rgba(96, 165, 250, 0.5)', borderRadius: '6px', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
            Save Settings
          </button>
        </div>
      </div>

      {/* Wipe Confirmation Modal */}
      {showWipeModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(8px)',
            zIndex: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '440px',
              backgroundColor: '#0f172a',
              border: '1px solid rgba(239, 68, 68, 0.4)',
              borderRadius: '12px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              color: '#f8fafc',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8)',
            }}
          >
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#ef4444', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShieldIcon size={18} color="#ef4444" />
              <span>💥 Confirm Factory Reset Node</span>
            </div>
            <p style={{ fontSize: '11px', color: '#cbd5e1', lineHeight: '1.5' }}>
              This action will perform a cryptographic erasure, wiping all active signing keypairs, room messages, and node configuration.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                Type <strong style={{ color: '#ef4444' }}>DESTROY</strong> to confirm:
              </label>
              <input
                type="text"
                value={wipeConfirmText}
                onChange={(e) => setWipeConfirmText(e.target.value)}
                placeholder="DESTROY"
                style={{ width: '100%', background: 'rgba(0, 0, 0, 0.4)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', padding: '8px 10px', color: '#fff', fontSize: '12px', outline: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
              <button
                onClick={() => {
                  setShowWipeModal(false);
                  setWipeConfirmText('');
                }}
                style={{ padding: '6px 12px', background: 'transparent', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '6px', color: '#94a3b8', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                disabled={wipeConfirmText !== 'DESTROY'}
                onClick={handleWipeDatabase}
                style={{
                  padding: '6px 14px',
                  background: wipeConfirmText === 'DESTROY' ? '#ef4444' : 'rgba(239, 68, 68, 0.2)',
                  border: 'none',
                  borderRadius: '6px',
                  color: wipeConfirmText === 'DESTROY' ? '#fff' : '#64748b',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: wipeConfirmText === 'DESTROY' ? 'pointer' : 'not-allowed',
                }}
              >
                💥 Confirm Factory Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Helper Node Registration Modal */}
      <HelperNodeRegistrationModal
        isOpen={showHelperRegistrationModal}
        onClose={() => setShowHelperRegistrationModal(false)}
        workerNodeId={nodeId}
        deviceKeyId={`devkey-${nodeId}`}
        displayName={profile?.displayName || formData?.displayName || `Peer ${nodeId}`}
        onSubmitProposal={(proposal) => {
          setPendingProposals((prev) => {
            const updated = [...prev.filter((p) => p.proposalId !== proposal.proposalId), proposal];
            if (typeof window !== 'undefined') {
              localStorage.setItem('mesh_pending_proposals', JSON.stringify(updated));
            }
            return updated;
          });
          triggerNotice('Participation proposal signed and submitted to Mesh Administrator.');
        }}
      />

      {/* Admin Lease Approval Modal */}
      <AdminLeaseApprovalModal
        isOpen={showAdminLeaseModal}
        onClose={() => setShowAdminLeaseModal(false)}
        pendingProposals={pendingProposals}
        onApproveProposal={(proposalId, grantedGrant, ttlHours) => {
          setPendingProposals((prev) => {
            const updated = prev.filter((p) => p.proposalId !== proposalId);
            if (typeof window !== 'undefined') {
              localStorage.setItem('mesh_pending_proposals', JSON.stringify(updated));
            }
            return updated;
          });
          triggerNotice(`Issued ${ttlHours}h capacity lease for helper node.`);
        }}
        onRejectProposal={(proposalId) => {
          setPendingProposals((prev) => {
            const updated = prev.filter((p) => p.proposalId !== proposalId);
            if (typeof window !== 'undefined') {
              localStorage.setItem('mesh_pending_proposals', JSON.stringify(updated));
            }
            return updated;
          });
          triggerNotice('Helper proposal rejected.');
        }}
      />

      {/* Avatar Photo Crop & Scale Modal */}
      <AvatarPhotoEditor
        isOpen={showPhotoEditor}
        onClose={() => setShowPhotoEditor(false)}
        currentAvatarUrl={formData.avatarUrl}
        onSave={(dataUrl) => {
          setFormData((prev) => ({ ...prev, avatarUrl: dataUrl }));
          triggerNotice('Avatar photo updated. Click Save to persist.');
        }}
      />
    </div>
  );
};
